import React, { useEffect, useRef, useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { PAGE_SIZE_MAX, PAGE_SIZE_MIN, PAGE_SIZE_PRESETS, clampPageSize } from './tablePageSize';
import styles from './Pagination.module.css';

function buildRange(page, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const out = new Set([1, total, page - 1, page, page + 1]);
  if (page <= 3) {
    [2, 3, 4].forEach((n) => out.add(n));
  }
  if (page >= total - 2) {
    [total - 1, total - 2, total - 3].forEach((n) => out.add(n));
  }
  const sorted = [...out].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i += 1) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      result.push('…');
    }
    result.push(sorted[i]);
  }
  return result;
}

export default function Pagination({
  page = 1,
  pageSize = 100,
  total = 0,
  onPageChange,
  /** `auto` fits rows to the table. `manual` uses `pageSize`. */
  sizeMode = 'manual',
  pageSizeOptions = PAGE_SIZE_PRESETS,
  onPageSizeChange,
  showPageSizeSelector = true
}) {
  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / Math.max(1, Number(pageSize) || 1)));
  const safePage = Math.min(Math.max(1, Number(page) || 1), totalPages);
  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(total, safePage * pageSize);
  const range = buildRange(safePage, totalPages);

  const goto = (n) => {
    if (n < 1 || n > totalPages || !onPageChange) return;
    const raw = Number(page);
    const urlPage = Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1;
    if (urlPage <= totalPages && n === urlPage) return;
    onPageChange(n);
  };

  const numericSize = clampPageSize(pageSize);
  const presetValues = pageSizeOptions.map((opt) => Number(opt)).filter((n) => Number.isFinite(n));
  const isCustomSize = sizeMode === 'manual' && !presetValues.includes(numericSize);
  const [customOpen, setCustomOpen] = useState(false);
  const [customDraft, setCustomDraft] = useState(String(numericSize));
  const customInputRef = useRef(null);
  const skipBlurCommit = useRef(false);
  const showCustom = customOpen || isCustomSize;
  const selectValue = sizeMode === 'auto' ? 'auto' : showCustom ? 'custom' : String(numericSize);

  useEffect(() => {
    if (!showCustom) return;
    setCustomDraft(String(numericSize));
  }, [numericSize, showCustom]);

  useEffect(() => {
    if (!customOpen) return;
    customInputRef.current?.focus();
    customInputRef.current?.select();
  }, [customOpen]);

  const commitValue = (raw) => {
    const parsed = parseInt(raw, 10);
    if (!Number.isFinite(parsed)) {
      setCustomDraft(String(numericSize));
      setCustomOpen(false);
      return;
    }
    const next = clampPageSize(parsed);
    setCustomDraft(String(next));
    setCustomOpen(false);
    if (sizeMode === 'manual' && next === numericSize) return;
    onPageSizeChange?.(next);
  };

  const commitCustom = () => {
    if (skipBlurCommit.current) {
      skipBlurCommit.current = false;
      return;
    }
    commitValue(customDraft);
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.summary}>
        Showing <strong>{start.toLocaleString()}</strong>–<strong>{end.toLocaleString()}</strong> of{' '}
        <strong>{total.toLocaleString()}</strong>
      </div>

      <div className={styles.controls}>
        {showPageSizeSelector && onPageSizeChange ? (
          <div className={styles.sizeSelect}>
            <label>
              <span>Rows</span>
              <select
                aria-label="Rows per page"
                value={selectValue}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'custom') {
                    setCustomDraft(String(numericSize));
                    setCustomOpen(true);
                    return;
                  }
                  skipBlurCommit.current = true;
                  setCustomOpen(false);
                  if (value === 'auto') {
                    if (sizeMode !== 'auto') onPageSizeChange('auto');
                    return;
                  }
                  const next = Number(value);
                  if (sizeMode === 'manual' && next === numericSize) return;
                  onPageSizeChange(next);
                }}
              >
                <option value="auto">Fit</option>
                {presetValues.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
                <option value="custom">Custom</option>
              </select>
            </label>
            {showCustom ? (
              <input
                ref={customInputRef}
                className={styles.sizeInput}
                type="number"
                inputMode="numeric"
                min={PAGE_SIZE_MIN}
                max={PAGE_SIZE_MAX}
                aria-label="Custom rows per page"
                value={customDraft}
                onChange={(e) => setCustomDraft(e.target.value)}
                onBlur={commitCustom}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitValue(e.currentTarget.value);
                  }
                }}
              />
            ) : null}
          </div>
        ) : null}

        <button
          type="button"
          className={styles.navBtn}
          onClick={() => goto(safePage - 1)}
          disabled={safePage <= 1}
          aria-label="Previous page"
        >
          <FaChevronLeft />
        </button>

        <ul className={styles.pages}>
          {range.map((n, idx) =>
            n === '…' ? (
              <li key={`gap-${idx}`} className={styles.gap}>
                …
              </li>
            ) : (
              <li key={n}>
                <button
                  type="button"
                  className={[styles.pageBtn, n === safePage ? styles.pageBtnActive : ''].join(' ')}
                  onClick={() => goto(n)}
                  aria-current={n === safePage ? 'page' : undefined}
                >
                  {n}
                </button>
              </li>
            )
          )}
        </ul>

        <button
          type="button"
          className={styles.navBtn}
          onClick={() => goto(safePage + 1)}
          disabled={safePage >= totalPages}
          aria-label="Next page"
        >
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
}
